import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';

// =====================================================
// TYPES & INTERFACES
// =====================================================

export interface Task {
  id: string;
  project_id: string;
  organization_id: string;
  section_id?: string | null;
  section_position?: number;
  title: string;
  description?: string;
  status: 'backlog' | 'todo' | 'in-progress' | 'review' | 'done' | 'blocked';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignee_id?: string;
  reviewer_id?: string;
  parent_task_id?: string;
  start_date?: string;
  due_date?: string;
  estimated_time: number;
  actual_time: number;
  labels: string[];
  position: number;
  ai_generated: boolean;
  ai_suggestions: Record<string, any>;
  custom_fields: Record<string, any>;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  is_recurring?: boolean;
  recurrence_rule?: any;
  recurrence_end_date?: string | null;
  // From view
  project_name?: string;
  project_status?: string;
  project_color?: string;
  section_name?: string;
  section_order?: number;
  subtask_count?: number;
  comment_count?: number;
  attachment_count?: number;
}

export interface TaskInput {
  project_id: string;
  organization_id: string;
  section_id?: string | null;
  section_position?: number;
  title: string;
  description?: string;
  status?: 'backlog' | 'todo' | 'in-progress' | 'review' | 'done' | 'blocked';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assignee_id?: string | null;
  reviewer_id?: string | null;
  parent_task_id?: string;
  start_date?: string;
  due_date?: string;
  estimated_time?: number;
  labels?: string[];
  position?: number;
  ai_generated?: boolean;
  custom_fields?: Record<string, any>;
  is_recurring?: boolean;
  recurrence_rule?: any;
  recurrence_end_date?: string | null;
  completed_at?: string | null;
}

export interface ListTasksParams {
  organization_id: string;
  project_id?: string;
  assignee_id?: string;
  status?: string;
  search?: string;
  due_before?: string;
  parent_task_id?: string;
  limit?: number;
  offset?: number;
}

// =====================================================
// HELPER: Get Auth Token
// =====================================================

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

// =====================================================
// API BASE URL
// =====================================================

const API_BASE_URL = `${ENV.API_URL}/api/project-management/tasks`;

// =====================================================
// TASKS SERVICE
// =====================================================

export const tasksService = {
  /**
   * List tasks with optional filters
   */
  async list(params: ListTasksParams): Promise<{ data: Task[]; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });

      const response = await fetch(`${API_BASE_URL}?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return { data: [], error: error.error || 'Failed to fetch tasks' };
      }

      const result = await response.json();
      return { data: result.data || [], error: null };
    } catch (err: any) {
      console.error('Error in tasksService.list:', err);
      return { data: [], error: err.message || 'Network error' };
    }
  },

  /**
   * Get task by ID with details
   */
  async getById(taskId: string, organizationId: string): Promise<{ data: any; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(`${API_BASE_URL}/${taskId}?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return { data: null, error: error.error || 'Failed to fetch task' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (err: any) {
      console.error('Error in tasksService.getById:', err);
      return { data: null, error: err.message || 'Network error' };
    }
  },

  /**
   * Create a new task
   */
  async create(input: TaskInput): Promise<{ data: Task | null; error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const error = await response.json();
        return { data: null, error: error.error || 'Failed to create task' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (err: any) {
      console.error('Error in tasksService.create:', err);
      return { data: null, error: err.message || 'Network error' };
    }
  },

  /**
   * Update a task
   */
  async update(
    taskId: string,
    organizationId: string,
    updates: Partial<TaskInput>
  ): Promise<{ data: Task | null; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(`${API_BASE_URL}/${taskId}?${queryParams.toString()}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const error = await response.json();
        return { data: null, error: error.error || 'Failed to update task' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (err: any) {
      console.error('Error in tasksService.update:', err);
      return { data: null, error: err.message || 'Network error' };
    }
  },

  /**
   * Bulk update multiple tasks
   */
  async bulkUpdate(
    taskIds: string[],
    organizationId: string,
    updates: Partial<TaskInput>
  ): Promise<{ data: Task[]; error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(`${API_BASE_URL}/bulk-update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ task_ids: taskIds, organization_id: organizationId, updates }),
      });

      if (!response.ok) {
        const error = await response.json();
        return { data: [], error: error.error || 'Failed to bulk update tasks' };
      }

      const result = await response.json();
      return { data: result.data || [], error: null };
    } catch (err: any) {
      console.error('Error in tasksService.bulkUpdate:', err);
      return { data: [], error: err.message || 'Network error' };
    }
  },

  /**
   * Delete a task
   */
  async delete(taskId: string, organizationId: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(`${API_BASE_URL}/${taskId}?${queryParams.toString()}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return { error: error.error || 'Failed to delete task' };
      }

      return { error: null };
    } catch (err: any) {
      console.error('Error in tasksService.delete:', err);
      return { error: err.message || 'Network error' };
    }
  },

  /**
   * Get tasks due today or overdue
   */
  async getUpcoming(organizationId: string, userId?: string): Promise<{ data: Task[]; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });
      if (userId) queryParams.append('user_id', userId);

      const response = await fetch(`${API_BASE_URL}/upcoming?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return { data: [], error: error.error || 'Failed to fetch upcoming tasks' };
      }

      const result = await response.json();
      return { data: result.data || [], error: null };
    } catch (err: any) {
      console.error('Error in tasksService.getUpcoming:', err);
      return { data: [], error: err.message || 'Network error' };
    }
  },
};

export default tasksService;
